import { COUNTRIES } from "@func-core/core/domain/constants";
import { PetSex, PetSpecies } from "@func-core/core/domain/view-models/";
import { CountryCode } from "@settings/countries";
import { ProductId } from "@settings/products";

type PetBreed = {
  kbKey: string;
  name: string;
};

type TrackingParams = {
  metaFbc: string | null;
  metaFbp: string | null;
};

type Product = {
  id: ProductId;
  name: string;
};

type Quote = {
  premium: number;
  product: Product;
  selected: boolean | null;
  uuid: string;
};

export type ResultStatus = "binding" | "completed" | "created" | "requested";

type PetInfo = {
  name: string;
  chip: string | null;
  species: PetSpecies;
  birthDate: string;
  sex: PetSex;
  breed: PetBreed;
  secondBreed: PetBreed | null;
  purchasedProducts: string[];
};

type PetParentInfo = {
  firstName: string | null;
  lastName: string | null;
  idNumber: string | null;
  address: string | null;
  city: string | null;
  zip: string | null;
  phone: string | null;
  kbKey: string;
  countryCode: string;
  salutation: string | null;
  birthDate: string | null;
};

type PolicyInfo = {
  startDate: string;
  signatureUrl: string | null;
  paymentIntervalMonths: number;
};

export type ResultViewModel = {
  uuid: string;
  email: string;
  status: ResultStatus;
  pet: PetInfo;
  petParent: PetParentInfo;
  policy: PolicyInfo;
  trackingParams: TrackingParams;
  country: CountryCode;
  currency: string;
  quotes: Quote[];
  leadCreatedAt: string;
  leadAgeInDays: number | null;
  leadHasCompletedResult: boolean;
};

export type PetParentData = {
  firstName: string | null;
  lastName: string | null;
  idNumber: string | null;
  salutation: string | null;
  birthDate: string | null;
  address: string | null;
  city: string | null;
  zip: string | null;
  countryCode: string;
  phone: string | null;
};

export type ResultDTO = {
  uuid: string;
  email: string;
  pet_name: string;
  pet_chip: string | null;
  pet_species: PetSpecies;
  pet_parent_kb_key: string;
  pet_birth_date: string;
  pet_sex: PetSex;
  pet_breed: PetBreed;
  policy_start_date: string;
  currency: string;
  signature_url: string | null;
  payment_interval_months: number;
  status: ResultStatus;
  pet_purchased_products: string[];
  pet_second_breed: PetBreed | null;
  tracking_params: TrackingParams;
  country: CountryCode;
  quotes: Quote[];
  lead_created_at: string;
  lead_has_completed_result: boolean;
};

export type HealthForecastDTO = {
  result: ResultDTO;
  forecast_bundle: {
    uuid: string;
    premium: number;
    product: Product;
  };
};

const calculateLeadAgeInDays = (leadCreatedAt: string): number | null => {
  if (!leadCreatedAt) return null;

  try {
    const createdDate = new Date(leadCreatedAt);
    const now = new Date();
    const diffTime = now.getTime() - createdDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return diffDays >= 0 ? diffDays : null;
  } catch (error) {
    return null;
  }
};

export const createResultViewModel = (
  data: ResultDTO,
  petParentData?: PetParentData | null
): ResultViewModel => {
  const defaultCountryCode = COUNTRIES[data.country].prefixPhoneNumber;

  const petParent = petParentData ?? {
    firstName: null,
    lastName: null,
    idNumber: null,
    salutation: null,
    birthDate: null,
    address: null,
    city: null,
    zip: null,
    countryCode: defaultCountryCode,
    phone: null,
  };

  return {
    uuid: data.uuid,
    email: data.email,
    status: data.status,
    pet: {
      name: data.pet_name,
      chip: data.pet_chip,
      species: data.pet_species,
      birthDate: data.pet_birth_date,
      sex: data.pet_sex,
      breed: data.pet_breed,
      secondBreed: data.pet_second_breed,
      purchasedProducts: data.pet_purchased_products,
    },
    petParent: {
      kbKey: data.pet_parent_kb_key,
      firstName: petParent.firstName,
      lastName: petParent.lastName,
      idNumber: petParent.idNumber,
      salutation: petParent.salutation,
      birthDate: petParent.birthDate,
      address: petParent.address,
      city: petParent.city,
      zip: petParent.zip,
      countryCode: petParent.countryCode,
      phone: petParent.phone,
    },
    policy: {
      startDate: data.policy_start_date,
      signatureUrl: data.signature_url,
      paymentIntervalMonths: data.payment_interval_months,
    },
    currency: data.currency,
    trackingParams: data.tracking_params,
    country: data.country,
    quotes: data.quotes,
    leadCreatedAt: data.lead_created_at,
    leadAgeInDays: calculateLeadAgeInDays(data.lead_created_at),
    leadHasCompletedResult: data.lead_has_completed_result,
  };
};
